import { getNativeAccessToken } from "./auth";
import {
  getNativeRoutingSnapshot,
  initializeNativeRegionRouting,
  isRussianNativeRoutingScenario,
  recoverNativeRegionRoutingAfterPrimaryFailure,
  recheckRussianNativeRegionRouting,
} from "./regionRouting";
import { isNativeApp } from "./runtime";

const FAILURE_WINDOW_MS = 30_000;
const FAILURE_THRESHOLD = 3;
const RECHECK_COOLDOWN_MS = 5_000;

let monitoringPromise = null;
let networkListener = null;
let failureTimestamps = [];
let lastRecheckAt = 0;
let recheckPromise = null;

export function shouldRecoverRussianNativeRoute() {
  return isNativeApp();
}

export async function recheckRussianNativeRoute(reason = "manual") {
  if (!shouldRecoverRussianNativeRoute()) return getNativeRoutingSnapshot();
  if (recheckPromise) return recheckPromise;

  const now = Date.now();
  if (now - lastRecheckAt < RECHECK_COOLDOWN_MS) return getNativeRoutingSnapshot();
  lastRecheckAt = now;

  if (isRussianNativeRoutingScenario()) {
    recheckPromise = recheckRussianNativeRegionRouting({
      accessToken: getNativeAccessToken(),
      reason,
    });
  } else {
    // The initializer keeps the healthy EU endpoint first and only hedges to
    // the fixed RU endpoint if the primary is slow or unavailable.
    recheckPromise = initializeNativeRegionRouting({
      accessToken: getNativeAccessToken(),
    });
  }
  recheckPromise = recheckPromise.finally(() => {
    recheckPromise = null;
  });
  return recheckPromise;
}

export async function recoverNativeRouteAfterTransportFailure() {
  if (!shouldRecoverRussianNativeRoute()) return null;

  const before = getNativeRoutingSnapshot();
  const after = await recoverNativeRegionRoutingAfterPrimaryFailure({
    accessToken: getNativeAccessToken(),
  });
  return after?.apiOrigin && (
    after.apiOrigin !== before?.apiOrigin
    || after.transport !== before?.transport
  ) ? after : null;
}

export function reportNativeTransportFailure() {
  if (!shouldRecoverRussianNativeRoute()) return;
  const now = Date.now();
  failureTimestamps = failureTimestamps.filter((timestamp) => now - timestamp <= FAILURE_WINDOW_MS);
  failureTimestamps.push(now);
  if (failureTimestamps.length < FAILURE_THRESHOLD) return;
  failureTimestamps = [];
  void recheckRussianNativeRoute("repeated_transport_failure");
}

export async function startRussianNativeNetworkRecovery() {
  if (!isNativeApp()) return null;
  if (monitoringPromise) return monitoringPromise;

  monitoringPromise = (async () => {
    const { Network } = await import("@capacitor/network");
    networkListener = await Network.addListener("networkStatusChange", (status) => {
      if (status?.connected) void recheckRussianNativeRoute("network_change");
    });
    window.addEventListener("online", handleBrowserOnline);
    window.addEventListener("vg:native-resume", handleNativeResume);
    return true;
  })().catch((error) => {
    monitoringPromise = null;
    throw error;
  });

  return monitoringPromise;
}

function handleBrowserOnline() {
  void recheckRussianNativeRoute("browser_online");
}

function handleNativeResume() {
  void recheckRussianNativeRoute("app_resume");
}

export async function stopRussianNativeNetworkRecoveryForTests() {
  await networkListener?.remove?.();
  networkListener = null;
  monitoringPromise = null;
  failureTimestamps = [];
  lastRecheckAt = 0;
  recheckPromise = null;
  if (typeof window !== "undefined") {
    window.removeEventListener("online", handleBrowserOnline);
    window.removeEventListener("vg:native-resume", handleNativeResume);
  }
}
